import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const getCurrentUser = () => api.get('/auth/me');

// Ministries
export const getMinistries = (year) => api.get(`/ministries?year=${year}`);
export const getMinistry = (id, year) => api.get(`/ministries/${id}?year=${year}`);
export const createMinistry = (formData) => {
  return api.post('/ministries', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const updateMinistry = (id, formData) => {
  return api.put(`/ministries/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const updateMinistryLogo = (id, formData) => {
  return api.put(`/ministries/${id}/logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const deleteMinistry = (id) => api.delete(`/ministries/${id}`);
export const archiveMinistry = (id) => api.put(`/ministries/${id}/archive`);
export const unarchiveMinistry = (id) => api.put(`/ministries/${id}/unarchive`);
export const getArchivedMinistries = (year) => api.get(`/ministries/archived/list?year=${year}`);
export const addAdjustment = (id, data) => api.post(`/ministries/${id}/adjustments`, data);
export const deleteAdjustment = (ministryId, adjustmentId) => api.delete(`/ministries/${ministryId}/adjustments/${adjustmentId}`);
export const getMinistryYears = (id) => api.get(`/ministries/${id}/years`);
export const parseOfferingsFile = (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/ministries/import-offerings/parse', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress
  });
};
export const confirmOfferingsImport = (data) => api.post('/ministries/import-offerings/confirm', data);

// Reimbursements
export const getReimbursements = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/reimbursements?${queryString}`);
};
export const getReimbursement = (id) => api.get(`/reimbursements/${id}`);
export const getReimbursementForClone = (id) => api.get(`/reimbursements/${id}/clone`);
export const createReimbursement = (formData, onUploadProgress) => {
  return api.post('/reimbursements', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress
  });
};
export const updateReimbursement = (id, formData, onUploadProgress) => {
  return api.put(`/reimbursements/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress
  });
};
export const approveReimbursement = (id) => api.post(`/reimbursements/${id}/approve`);
export const rejectReimbursement = (id, reason) => api.post(`/reimbursements/${id}/reject`, { reason });
export const payReimbursement = (id) => api.post(`/reimbursements/${id}/pay`);
export const markReimbursementAsPaid = (id, formData) => {
  return api.post(`/reimbursements/${id}/pay`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const deleteReimbursement = (id) => api.delete(`/reimbursements/${id}`);

// Assets (Patrimônio)
export const getAssets = (ministryId) => api.get(`/assets?ministry=${ministryId}`);
export const getAsset = (id) => api.get(`/assets/${id}`);
export const getAssetCount = (ministryId) => api.get(`/assets/count/${ministryId}`);
export const getPendingTransfers = (ministryId) => api.get(`/assets/pending-transfers?ministry=${ministryId}`);
export const createAsset = (formData, onUploadProgress) => {
  return api.post('/assets', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  });
};
export const updateAsset = (id, formData, onUploadProgress) => {
  return api.put(`/assets/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress
  });
};
export const deleteAsset = (id) => api.delete(`/assets/${id}`);
export const transferAsset = (id, data) => api.post(`/assets/${id}/transfer`, data);
export const confirmTransfer = (id) => api.post(`/assets/${id}/confirm-transfer`);
export const rejectTransfer = (id) => api.post(`/assets/${id}/reject-transfer`);

// Elections (Votações) — secretaria/admin
export const getElections = () => api.get('/elections');
export const getElection = (id) => api.get(`/elections/${id}`);
export const createElection = (data) => api.post('/elections', data);
export const updateElection = (id, data) => api.put(`/elections/${id}`, data);
export const deleteElection = (id) => api.delete(`/elections/${id}`);
export const updateElectionMinistry = (id, mid, data) => api.put(`/elections/${id}/ministries/${mid}`, data);
// Etapa derivada: ex. Primeiro Ancião entre os eleitos do Ancionato
export const deriveMinistry = (id, mid, data) => api.post(`/elections/${id}/ministries/${mid}/derive`, data);
export const deleteDerivedMinistry = (id, mid) => api.delete(`/elections/${id}/ministries/${mid}`);
export const addCandidate = (id, mid, data) => api.post(`/elections/${id}/ministries/${mid}/candidates`, data);
export const updateCandidate = (id, mid, cid, data) => api.put(`/elections/${id}/ministries/${mid}/candidates/${cid}`, data);
export const deleteCandidate = (id, mid, cid) => api.delete(`/elections/${id}/ministries/${mid}/candidates/${cid}`);
// Discussão de nomes: data = { updates: [{ cid, state }] } ou { action: 'finish_now' | 'reset' }
export const updateDiscussion = (id, mid, data) => api.post(`/elections/${id}/ministries/${mid}/discussion`, data);
export const openRound = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/rounds/open`);
export const closeRound = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/rounds/close`);
// Chama quem ainda não votou: alarme sonoro/vibração nos celulares que não votaram nesta rodada
export const nudgeRound = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/rounds/nudge`);
// Cancela a votação aberta sem calcular resultado (descarta a rodada e os votos)
export const cancelRound = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/rounds/cancel`);
export const voidRound = (id, mid, round) => api.post(`/elections/${id}/ministries/${mid}/rounds/${round}/void`);
export const resetMinistry = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/reset`);
export const setElected = (id, mid, name) => api.post(`/elections/${id}/ministries/${mid}/set-elected`, { name });
export const setMinistryStatus = (id, mid, status) => api.post(`/elections/${id}/ministries/${mid}/status`, { status });
export const applyLeader = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/apply-leader`);
export const rejectNominee = (id, mid, round, cid, data) => api.post(`/elections/${id}/ministries/${mid}/rounds/${round}/nominated/${cid}/reject`, data);
export const confirmNominees = (id, mid) => api.post(`/elections/${id}/ministries/${mid}/confirm`);
// Telão: destaca um ministério (view: 'names' | 'result' | 'elected') ou limpa (mid = null)
export const featureMinistry = (id, mid, view) => api.post(`/elections/${id}/feature`, { mid: mid || null, view });
// Remove 1 presença (check-in anônimo mais recente)
export const removeOneCheckin = (id) => api.post(`/elections/${id}/checkins/remove-one`);
// Nova sessão de presença (novo dia): zera os presentes
export const newPresenceSession = (id) => api.post(`/elections/${id}/checkins/new-session`);

// Votação pública (sem autenticação)
export const getVoteState = (code, token) => api.get(`/vote/state?code=${encodeURIComponent(code)}${token ? `&token=${encodeURIComponent(token)}` : ''}`);
export const voteCheckin = (code, token) => api.post('/vote/checkin', { code, token });
export const voteCheckout = (code, token) => api.post('/vote/checkout', { code, token });
export const castVote = (code, token, candidateIds) => api.post('/vote', { code, token, candidateIds: Array.isArray(candidateIds) ? candidateIds : [candidateIds] });

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

export default api;
